/**
 * TypeScript types for Schedule Builder (Feature 004)
 */

// ============================================================================
// Enums
// ============================================================================

export type ScheduleType = 'routine' | 'daily_schedule' | 'first_then' | 'choice_board' | 'waiting_board' | 'weekly_schedule';

export type DisplayMode = 'single_focus' | 'timeline' | 'checklist' | 'grid' | 'first_then';

// Routine-specific visualization modes
export type RoutineVisualization = 'overview_only' | 'overview_then_fullscreen' | 'fullscreen_only';

export type CelebrationLevel = 'none' | 'minimal' | 'normal' | 'maximum';

export type CompletionAction = 'checkmark' | 'tap' | 'swipe' | 'hold' | 'auto' | 'parent_only';

export type TimerStyle = 'shrinking_disk' | 'shrinking_bar' | 'countdown' | 'hourglass' | 'sand_timer' | 'progress_bar' | 'traffic_light' | 'character';

export type RecurrenceType = 'manual' | 'daily' | 'weekly' | 'weekdays' | 'weekends' | 'one_time';

export type DayOfWeek = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export type StepType = 'action' | 'schedule';

// Weekly Schedule specific types
export type WeekReferenceType = 'specific' | 'recurring';

// ============================================================================
// Interfaces
// ============================================================================

export interface ScheduleIcon {
  type: 'action' | 'emoji';
  actionId?: string | null;
  emoji?: string | null;
}

export interface ScheduleRewards {
  baseTokens: number;
  bonusUnderTimeSeconds?: number | null;
  bonusUnderTimeTokens?: number | null;
  bonusNoSkipsTokens?: number | null;
}

export interface ScheduleSettings {
  allowSkipSteps: boolean;
  requireCompletionConfirmation: boolean;
  autoAdvanceDelayMs?: number | null;
  celebrationLevel: CelebrationLevel;
  routineVisualization?: RoutineVisualization | null;  // Only for routine type schedules
}

export interface ScheduleRecurrence {
  type: RecurrenceType;
  daysOfWeek?: DayOfWeek[] | null;
  startTime?: string | null;  // HH:mm format
  endTime?: string | null;    // HH:mm format
  startDate?: number | null;  // Unix ms
  endDate?: number | null;    // Unix ms
  notifyBeforeMinutes?: number | null;
}

export interface StepTimer {
  durationSeconds: number;
  warningSeconds?: number | null;
  style?: TimerStyle | null;
}

// Weekly Schedule specific interfaces
export interface WeekReference {
  type: WeekReferenceType;
  weekStartDate?: number | null;  // Unix ms, Monday of the specific week (only for 'specific' type)
}

// Step data as stored in Firebase (without the id field, since id is the key)
export type StepData = Omit<ScheduleStep, 'id'>;

export interface WeeklyDayConfig {
  isEnabled: boolean;
  steps?: Record<string, StepData> | null;  // Map of step_id -> step data (id is the key)
  overrideSettings?: ScheduleSettings | null;   // Optional day-specific settings
}

export interface WeeklyScheduleDays {
  MON?: WeeklyDayConfig | null;
  TUE?: WeeklyDayConfig | null;
  WED?: WeeklyDayConfig | null;
  THU?: WeeklyDayConfig | null;
  FRI?: WeeklyDayConfig | null;
  SAT?: WeeklyDayConfig | null;
  SUN?: WeeklyDayConfig | null;
}

export interface ScheduleStep {
  id: string;
  order: number;
  title: string;
  subtitle?: string | null;
  stepType: StepType;
  actionId?: string | null;
  scheduleId?: string | null;
  scheduledTime?: string | null;  // HH:mm format
  timer?: StepTimer | null;
  isSkippable: boolean;
  completionAction: CompletionAction;
  helpPrompt?: string | null;
  tokenValue: number;
  isRewardStep: boolean;
  createdAt: number;  // Unix timestamp in milliseconds
  updatedAt: number;
}

export interface Schedule {
  id: string;
  type: ScheduleType;
  name: string;
  description?: string | null;
  icon: ScheduleIcon;
  displayMode: DisplayMode;
  estimatedDurationSeconds?: number | null;
  rewards: ScheduleRewards;
  settings: ScheduleSettings;
  recurrence?: ScheduleRecurrence | null;
  steps?: ScheduleStep[] | null;
  isActive: boolean;
  isTemplate: boolean;
  sortOrder: number;
  createdBy: string;  // Firebase UID
  createdAt: number;  // Unix timestamp in milliseconds
  updatedAt: number;
  // Weekly schedule specific fields (only used when type is 'weekly_schedule')
  weekReference?: WeekReference | null;
  days?: WeeklyScheduleDays | null;
}

// ============================================================================
// Create/Update Models
// ============================================================================

export interface ScheduleCreate {
  type: ScheduleType;
  name: string;
  description?: string | null;
  icon?: ScheduleIcon | null;
  displayMode?: DisplayMode | null;
  settings?: ScheduleSettings | null;
  // Weekly schedule specific
  weekReference?: WeekReference | null;
}

export interface ScheduleUpdate {
  name?: string;
  description?: string | null;
  icon?: ScheduleIcon;
  displayMode?: DisplayMode;
  rewards?: ScheduleRewards;
  settings?: ScheduleSettings;
  recurrence?: ScheduleRecurrence | null;
  isActive?: boolean;
  sortOrder?: number;
  // Weekly schedule specific
  weekReference?: WeekReference | null;
}

export interface StepCreate {
  title: string;
  subtitle?: string | null;
  stepType: StepType;
  actionId?: string | null;
  scheduleId?: string | null;
  scheduledTime?: string | null;
  timer?: StepTimer | null;
  isSkippable?: boolean;
  completionAction?: CompletionAction;
  helpPrompt?: string | null;
  tokenValue?: number;
  isRewardStep?: boolean;
}

export interface StepUpdate {
  title?: string;
  subtitle?: string | null;
  stepType?: StepType;
  actionId?: string | null;
  scheduleId?: string | null;
  scheduledTime?: string | null;
  timer?: StepTimer | null;
  isSkippable?: boolean;
  completionAction?: CompletionAction;
  helpPrompt?: string | null;
  tokenValue?: number;
  isRewardStep?: boolean;
}

// ============================================================================
// Response Models
// ============================================================================

export interface ScheduleListResponse {
  schedules: Schedule[];
  count: number;
}

export interface StepReorderRequest {
  step_ids: string[];
}

// Weekly schedule specific request models
export interface WeeklyDayUpdateRequest {
  isEnabled?: boolean;
  overrideSettings?: ScheduleSettings | null;
}

export interface WeeklyDayCopyRequest {
  clearExisting: boolean;
}

// ============================================================================
// Validation Constants
// ============================================================================

export const SCHEDULE_VALIDATION = {
  NAME_MIN_LENGTH: 1,
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 500,
  STEP_TITLE_MIN_LENGTH: 1,
  STEP_TITLE_MAX_LENGTH: 100,
  STEP_SUBTITLE_MAX_LENGTH: 200,
  HELP_PROMPT_MAX_LENGTH: 500,
  TIMER_MIN_SECONDS: 5,
  TIMER_MAX_SECONDS: 3600,
  TOKEN_MIN_VALUE: 0,
  TOKEN_MAX_VALUE: 100,
} as const;

/** The tablet shows the first 3 choices of a choice board, by order (BA-87); the editor adds no more (BA-92) */
export const CHOICE_BOARD_MAX_CHOICES = 3;

// ============================================================================
// Default Values & Helpers
// ============================================================================

export const DEFAULT_SCHEDULE_ICON: Record<ScheduleType, ScheduleIcon> = {
  routine: { type: 'emoji', emoji: '📋' },
  daily_schedule: { type: 'emoji', emoji: '📅' },
  first_then: { type: 'emoji', emoji: '➡️' },
  choice_board: { type: 'emoji', emoji: '🔲' },
  waiting_board: { type: 'emoji', emoji: '⏳' },
  weekly_schedule: { type: 'emoji', emoji: '📆' },
};

export const DEFAULT_DISPLAY_MODE: Record<ScheduleType, DisplayMode> = {
  routine: 'single_focus',
  daily_schedule: 'timeline',
  first_then: 'first_then',
  choice_board: 'grid',
  waiting_board: 'single_focus',
  weekly_schedule: 'timeline',
};

export const DEFAULT_WEEK_REFERENCE: WeekReference = {
  type: 'recurring',
  weekStartDate: null,
};

export const ALL_DAYS_OF_WEEK: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export const DAY_LABELS: Record<DayOfWeek, string> = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
};

export const DEFAULT_REWARDS: ScheduleRewards = {
  baseTokens: 0,
  bonusUnderTimeSeconds: null,
  bonusUnderTimeTokens: null,
  bonusNoSkipsTokens: null,
};

export const DEFAULT_SETTINGS: ScheduleSettings = {
  allowSkipSteps: false,
  requireCompletionConfirmation: true,
  autoAdvanceDelayMs: null,
  celebrationLevel: 'normal',
  routineVisualization: 'overview_then_fullscreen',  // Default for routines
};

/**
 * Format duration in seconds to human-readable format
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format timer duration for display (e.g., "2:00")
 */
export function formatTimerDisplay(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Calculate total estimated duration for a schedule from its steps
 */
export function calculateEstimatedDuration(steps: ScheduleStep[]): number {
  return steps.reduce((total, step) => {
    if (step.timer?.durationSeconds) {
      return total + step.timer.durationSeconds;
    }
    // Default 60 seconds for steps without timer
    return total + 60;
  }, 0);
}

/**
 * Convert steps map (from Firebase) to sorted array
 */
export function stepsMapToArray(stepsMap: Record<string, ScheduleStep> | null | undefined): ScheduleStep[] {
  if (!stepsMap) return [];
  return Object.entries(stepsMap)
    .map(([id, step]) => ({ ...step, id }))
    .sort((a, b) => a.order - b.order);
}

/**
 * Get steps for a specific day in a weekly schedule
 */
export function getDaySteps(days: WeeklyScheduleDays | null | undefined, day: DayOfWeek): ScheduleStep[] {
  if (!days) return [];
  const dayConfig = days[day];
  if (!dayConfig?.steps) return [];
  return stepsMapToArray(dayConfig.steps as Record<string, ScheduleStep>);
}

/**
 * Get the Monday of the week containing the given date
 */
export function getWeekStartDate(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Format week range for display (e.g., "Dec 23 - Dec 29, 2024")
 */
export function formatWeekRange(weekStartDate: number): string {
  const start = new Date(weekStartDate);
  const end = new Date(weekStartDate);
  end.setDate(end.getDate() + 6);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();
  const year = end.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
}

// ============================================================================
// Device Schedule Settings (device-level defaults)
// ============================================================================

export interface DayColors {
  MON: string;
  TUE: string;
  WED: string;
  THU: string;
  FRI: string;
  SAT: string;
  SUN: string;
}

export interface DeviceScheduleSettings {
  defaultCompletionAction: CompletionAction;
  defaultTimerStyle: TimerStyle;
  dayColorsEnabled: boolean;
  dayColors: DayColors;
  updatedAt?: number | null;
  updatedBy?: string | null;
}

export interface DeviceScheduleSettingsUpdate {
  defaultCompletionAction?: CompletionAction;
  defaultTimerStyle?: TimerStyle;
  dayColorsEnabled?: boolean;
  dayColors?: DayColors;
}

export const DEFAULT_DAY_COLORS: DayColors = {
  MON: '#FFEB3B',  // Yellow
  TUE: '#F44336',  // Red
  WED: '#4CAF50',  // Green
  THU: '#2196F3',  // Blue
  FRI: '#9C27B0',  // Purple
  SAT: '#FF9800',  // Orange
  SUN: '#E91E63',  // Pink
};

export const DEFAULT_SCHEDULE_SETTINGS: DeviceScheduleSettings = {
  defaultCompletionAction: 'tap',
  defaultTimerStyle: 'shrinking_disk',
  dayColorsEnabled: false,
  dayColors: DEFAULT_DAY_COLORS,
};
